// Phone number product pages (DID, Toll-Free, Mobile, 2-Way, UIFN). Based on the CLI Secure phone numbers page,
// in VOIP2 branding. Coverage lists come from the live /api/countries data, so no country counts are hard-coded here.

const SCREENS = [
  { img: '/media/numbers/screen-buy.webp', title: 'Pick and buy numbers', body: 'Choose a country, number type and city, then add numbers to your basket.' },
  { img: '/media/numbers/screen-routing.webp', title: 'Route every call', body: 'Send calls to a SIP trunk, Cloud PBX, mobile or the AI Receptionist, with failover and time-of-day rules.' },
];

const STEPS = [
  { verb: 'Choose', body: 'Pick a country, number type and area from the live coverage list.' },
  { verb: 'Verify', body: 'Upload any documents the country requires. Many destinations need none.' },
  { verb: 'Route', body: 'Point the number at your PBX, SIP trunk, mobile or AI Receptionist.' },
  { verb: 'Go live', body: 'Take calls as soon as the number is active, and change routing at any time.' },
];

const COMMON_FAQS = [
  { q: 'Do I need documents to activate a number?', a: 'It depends on the country. Some numbers activate with no paperwork; others need proof of address or a local business registration. Each country page lists exactly what is required.' },
  { q: 'Can I port numbers I already have?', a: 'Yes. We port numbers from your current provider with no loss of service, and keep your routing in place while the port completes.' },
  { q: 'Where can calls be delivered?', a: 'To your VOIP2 Cloud PBX, a SIP trunk to any PBX, a mobile or landline, or the AI Receptionist, with failover to the next destination if the first is unavailable.' },
];

const related = (...keys) => {
  const all = {
    did: { label: 'DID International Numbers', to: '/phone-numbers/did-international' },
    tollfree: { label: 'Toll-Free Numbers', to: '/phone-numbers/toll-free' },
    mobile: { label: 'Mobile Numbers', to: '/phone-numbers/mobile' },
    twoway: { label: '2-Way Numbers', to: '/phone-numbers/2way' },
    uifn: { label: 'UIFN Numbers', to: '/phone-numbers/uifn' },
    sip: { label: 'SIP Trunking', to: '/products/sip-trunking' },
    pbx: { label: 'Cloud PBX', to: '/products/cloud-pbx' },
  };
  return keys.map((k) => all[k]);
};

export const NUMBER_PAGES = {
  did: {
    short: 'DID',
    seo: { title: 'DID International Numbers in 140+ Countries', path: '/phone-numbers/did-international', description: 'Local and national DID numbers in 140+ countries, routed to your PBX, SIP trunk or mobile. Build a local presence without a local office.' },
    crystal: '/media/icons/did-international.webp',
    filter: (c) => c.local || c.national,
    hero: {
      title: 'Local numbers in',
      highlight: '140+ countries',
      sub: 'Give your business a local presence anywhere. Customers dial a local or national number, and the call reaches your team wherever they work.',
      bullets: ['Local and national ranges', 'Route to any destination', 'Lawful porting', 'One portal for every country'],
    },
    trust: [
      { value: '140+', label: 'countries' },
      { value: 'Local', label: 'and national ranges' },
      { value: 'Minutes', label: 'to activate most numbers' },
      { value: '24/7', label: 'UK-based support' },
    ],
    intro: {
      title: 'What is a DID number?',
      paragraphs: [
        'A DID (Direct Inward Dialling) number is a real phone number in a city or country of your choice. Callers dial it as a normal local call, and VOIP2 delivers the call to your phone system over the internet.',
        'You can sell into a new market, open a support line in another country or give remote staff a local number, all without opening an office there.',
      ],
      bullets: ['City-level local numbers', 'Nationwide national numbers', 'Keep or port existing numbers', 'Change routing at any time'],
    },
    benefits: [
      { icon: 'MapPin', title: 'Local presence anywhere', body: 'Customers see a local number, so they pay local rates and are more likely to call.' },
      { icon: 'Route', title: 'Route to any device', body: 'Deliver calls to a PBX, SIP trunk, mobile, landline or the AI Receptionist.' },
      { icon: 'RefreshCw', title: 'Failover built in', body: 'If the first destination is unavailable, calls move down your failover chain.' },
      { icon: 'Clock', title: 'Time-of-day routing', body: 'Send calls to different teams by hours, weekends and holidays.' },
      { icon: 'BadgeCheck', title: 'Compliant by country', body: 'Each number meets local regulatory rules, with the requirements shown up front.' },
      { icon: 'ChartColumn', title: 'Live reporting', body: 'Inbound minutes, call history and trends for every number.' },
    ],
    useCases: [
      { icon: 'Globe', title: 'International sales', body: 'Sell into new countries with an in-country number.' },
      { icon: 'Headphones', title: 'Contact centres', body: 'Route international calls to agents anywhere.' },
      { icon: 'Building2', title: 'Remote offices', body: 'Give each team a local number without local lines.' },
      { icon: 'Server', title: 'Carriers and resellers', body: 'Extend your coverage with wholesale DIDs.' },
    ],
    faqs: [
      { q: 'What is the difference between local and national numbers?', a: 'A local number belongs to a specific city or area code. A national number is not tied to one area and is recognised across the whole country. Both reach the same destination.' },
      ...COMMON_FAQS,
    ],
    countryOptions: ['UK', 'USA / Canada', 'Europe', 'Asia-Pacific', 'Africa / Middle East', 'Multiple countries'],
    service: 'DID International Numbers',
    related: related('tollfree', 'sip', 'pbx'),
  },

  tollfree: {
    short: 'toll-free',
    seo: { title: 'International Toll-Free Numbers (ITFS)', path: '/phone-numbers/toll-free', description: 'International toll-free (ITFS) numbers your customers can call for free, routed to your PBX, SIP trunk or contact centre.' },
    crystal: '/media/icons/toll-free.webp',
    filter: (c) => c.itfs,
    hero: {
      title: 'Numbers your customers',
      highlight: 'call for free',
      sub: 'Toll-free (ITFS) numbers make it free and easy for customers to reach you, at home and abroad. You pay a simple inbound rate; they pay nothing.',
      bullets: ['Free for callers', 'Country-specific ranges', 'Port existing 0800s', 'Route to any destination'],
    },
    trust: [
      { value: 'Free', label: 'for your callers' },
      { value: 'ITFS', label: 'international toll-free' },
      { value: 'Minutes', label: 'to activate most numbers' },
      { value: '24/7', label: 'UK-based support' },
    ],
    intro: {
      title: 'Make it free to call you',
      paragraphs: [
        'A toll-free number, such as a UK 0800 or a US 1-800, costs your customers nothing to call. That simple fact means more calls answered and a more established brand.',
        'With international toll-free (ITFS) numbers you can offer the same free line in each country you serve, all managed and routed from one VOIP2 portal.',
      ],
      bullets: ['UK 0800 and international ITFS', 'One portal for every country', 'Detailed per-number reporting', 'Port the numbers you already use'],
    },
    benefits: [
      { icon: 'PhoneForwarded', title: 'Free for customers', body: 'Callers pay nothing, so they are more likely to pick up the phone.' },
      { icon: 'BadgeCheck', title: 'An established image', body: 'A freephone line signals a business that is easy to deal with.' },
      { icon: 'Route', title: 'Smart routing', body: 'Route by time of day, send overflow to another site and fail over automatically.' },
      { icon: 'ChartColumn', title: 'Measure campaigns', body: 'Use a separate number per campaign and see the calls each one drives.' },
      { icon: 'Repeat', title: 'Keep your number', body: 'Port existing toll-free numbers to VOIP2 with no loss of service.' },
      { icon: 'Globe', title: 'Global coverage', body: 'Toll-free numbers in many countries, all managed in one place.' },
    ],
    useCases: [
      { icon: 'Headphones', title: 'Customer support', body: 'A free helpline customers are happy to use.' },
      { icon: 'Briefcase', title: 'Sales lines', body: 'Remove any reason not to call your sales team.' },
      { icon: 'ShoppingBag', title: 'Orders and bookings', body: 'Free order and reservation lines.' },
      { icon: 'Megaphone', title: 'Marketing campaigns', body: 'Trackable numbers for ads and print.' },
    ],
    faqs: [
      { q: 'Who pays for calls to a toll-free number?', a: 'You do. Callers pay nothing, and you pay a per-minute inbound rate for the calls you receive. Rates vary by country and by whether the caller is on a landline or mobile.' },
      { q: 'What is the difference between toll-free and UIFN?', a: 'A toll-free number is free to call within one country. A UIFN is a single +800 freephone number that callers can dial free from many countries.' },
      ...COMMON_FAQS,
    ],
    countryOptions: ['UK (0800)', 'USA / Canada', 'Europe', 'Asia-Pacific', 'Multiple countries'],
    service: 'Toll-Free Numbers',
    related: related('uifn', 'did', 'pbx'),
  },

  mobile: {
    short: 'mobile',
    seo: { title: 'Virtual Mobile Numbers for Voice & SMS', path: '/phone-numbers/mobile', description: 'Virtual mobile numbers from genuine mobile ranges for voice and SMS, without SIM cards. Ready for apps, field teams and verification.' },
    crystal: '/media/icons/mobile.webp',
    filter: (c) => c.mobile || c.twoway,
    hero: {
      title: 'Mobile numbers for',
      highlight: 'voice and SMS',
      sub: 'Real mobile ranges, recognised by networks worldwide, delivered to your platform over SIP and API. Ready for apps, field teams and verification.',
      bullets: ['Voice and SMS', 'Recognised mobile ranges', 'API and webhooks', 'Route to any destination'],
    },
    trust: [
      { value: 'Voice', label: 'and SMS on one number' },
      { value: 'API', label: 'and webhooks included' },
      { value: 'Minutes', label: 'to activate most numbers' },
      { value: '24/7', label: 'UK-based support' },
    ],
    intro: {
      title: 'A mobile number without a SIM',
      paragraphs: [
        'VOIP2 mobile numbers come from genuine mobile ranges, so they look and behave like any other mobile number to the people who call or text them.',
        'Calls arrive over SIP and messages through our API, so you can use them in apps, CRMs, contact centres and verification flows without handing out SIM cards.',
      ],
      bullets: ['Genuine mobile ranges', 'Inbound SMS by webhook', 'Calls to your PBX or app', 'Number pools for scale'],
    },
    benefits: [
      { icon: 'Smartphone', title: 'Genuine mobile ranges', body: 'Numbers callers recognise as mobile, which many people prefer to call or text.' },
      { icon: 'MessageSquare', title: 'Voice and SMS', body: 'Receive calls and text messages on the same number.' },
      { icon: 'Zap', title: 'Real-time API', body: 'Inbound messages and call events delivered to your systems by webhook.' },
      { icon: 'Gauge', title: 'High throughput', body: 'Handle large volumes of calls and messages reliably.' },
      { icon: 'ShieldCheck', title: 'Carrier compliant', body: 'Meets mobile carrier and A2P messaging rules in each country.' },
      { icon: 'Route', title: 'Flexible routing', body: 'Deliver calls to a PBX, SIP trunk, app or another number.' },
    ],
    useCases: [
      { icon: 'KeyRound', title: 'Verification', body: 'Sender numbers for OTP and flash calls.' },
      { icon: 'Headphones', title: 'Mobile-first support', body: 'Let customers call or text the same number.' },
      { icon: 'Users', title: 'Field teams', body: 'A business mobile number on any device.' },
      { icon: 'Lock', title: 'Number masking', body: 'Private calls between drivers, riders and customers.' },
    ],
    faqs: [
      { q: 'Can mobile numbers receive SMS?', a: 'Yes, where the country supports it. Inbound messages are delivered to your system by webhook, and you can see SMS support per country on each country page.' },
      { q: 'What is the difference between mobile and 2-Way numbers?', a: 'A mobile number receives calls and, where supported, SMS. A 2-Way number also lets you make outbound calls showing that number as your caller ID.' },
      ...COMMON_FAQS,
    ],
    countryOptions: ['UK', 'USA / Canada', 'Europe', 'Asia-Pacific', 'Multiple countries'],
    service: 'Mobile Numbers',
    related: related('twoway', 'did', 'sip'),
  },

  twoway: {
    short: '2-Way',
    seo: { title: '2-Way Numbers: Make and Receive Calls', path: '/phone-numbers/2way', description: 'Make and receive calls on the same number, so customers always see a number they can call back.' },
    crystal: '/media/icons/2way.webp',
    filter: (c) => c.twoway,
    hero: {
      title: 'Call out and take calls',
      highlight: 'on one number',
      sub: 'With 2-Way numbers your outbound calls show the same local number customers can call back, so more calls get answered and every reply reaches you.',
      bullets: ['Inbound and outbound', 'Local caller ID', 'Works with any PBX', 'Voice and SMS where supported'],
    },
    trust: [
      { value: '2-Way', label: 'inbound and outbound' },
      { value: 'Local', label: 'caller ID abroad' },
      { value: 'Minutes', label: 'to activate most numbers' },
      { value: '24/7', label: 'UK-based support' },
    ],
    intro: {
      title: 'One number for both directions',
      paragraphs: [
        'Most virtual numbers only receive calls. A 2-Way number also lets you place outbound calls from it, so the person you call sees a familiar local number and can call you straight back.',
        'Customers are far more likely to answer a local number than an unknown international one, which makes 2-Way numbers ideal for sales, collections and appointment reminders.',
      ],
      bullets: ['Outbound caller ID you own', 'Callbacks reach the same team', 'Use with Cloud PBX or SIP', 'Conversational SMS where supported'],
    },
    benefits: [
      { icon: 'ArrowLeftRight', title: 'Both directions', body: 'Make and receive calls on the same number from your PBX or softphone.' },
      { icon: 'MapPin', title: 'Local caller ID', body: 'Present a local number in each market, so more calls are answered.' },
      { icon: 'PhoneForwarded', title: 'Callbacks come home', body: 'Missed calls ring back to the right team, not an unknown line.' },
      { icon: 'MessageSquare', title: 'Two-way SMS', body: 'Send and receive messages on the same number where the country supports it.' },
      { icon: 'ShieldCheck', title: 'Compliant caller ID', body: 'You only present numbers you own, in line with each country’s rules.' },
      { icon: 'ChartColumn', title: 'Full call history', body: 'Every inbound and outbound call in one report.' },
    ],
    useCases: [
      { icon: 'Briefcase', title: 'Outbound sales', body: 'Higher answer rates with local caller ID.' },
      { icon: 'Headphones', title: 'Customer support', body: 'Call customers back from the number they know.' },
      { icon: 'CalendarDays', title: 'Reminders', body: 'Appointment calls customers can reply to.' },
      { icon: 'Wallet', title: 'Collections', body: 'A trusted number for payment follow-ups.' },
    ],
    faqs: [
      { q: 'Can I make and receive calls on the same number?', a: 'Yes, that is what a 2-Way number is for. Outbound calls show the number as your caller ID, and inbound calls are delivered to your PBX, SIP trunk or mobile.' },
      { q: 'Do 2-Way numbers support SMS?', a: 'In many countries, yes. SMS support is listed per country, and inbound messages can be delivered to your system by webhook.' },
      ...COMMON_FAQS,
    ],
    countryOptions: ['UK', 'USA / Canada', 'Europe', 'Asia-Pacific', 'Multiple countries'],
    service: '2-Way Numbers',
    related: related('mobile', 'did', 'pbx'),
  },

  uifn: {
    short: 'UIFN',
    seo: { title: 'UIFN: International Freephone +800 Numbers', path: '/phone-numbers/uifn', description: 'One Universal International Freephone Number (+800) that callers can dial free from many countries.' },
    crystal: '/media/icons/uifn.webp',
    filter: (c) => c.uifn,
    hero: {
      title: 'One freephone number',
      highlight: 'for the world',
      sub: 'A Universal International Freephone Number (UIFN) is a single +800 number that customers can dial free from every participating country, wherever they are.',
      bullets: ['One +800 number', 'Free for callers', 'ITU allocated', 'Route by caller country'],
    },
    trust: [
      { value: '+800', label: 'global prefix' },
      { value: '45+', label: 'participating countries' },
      { value: 'Free', label: 'for your callers' },
      { value: 'ITU', label: 'allocated and regulated' },
    ],
    intro: {
      title: 'What is a UIFN?',
      paragraphs: [
        'UIFNs are global freephone numbers that start with the +800 country code. They are allocated by the International Telecommunication Union (ITU), so the same eight-digit number works in every participating country.',
        'Instead of publishing a different toll-free number in each market, you print one number on every product, website and ticket, and VOIP2 routes each call to the right team.',
      ],
      bullets: ['Same number in every country', 'Free from participating networks', 'Route by country of origin', 'Ideal for global brands'],
    },
    benefits: [
      { icon: 'Globe', title: 'One global number', body: 'A single number for every market, easy to print and remember.' },
      { icon: 'PhoneForwarded', title: 'Free for callers', body: 'Callers dial free from participating fixed and mobile networks.' },
      { icon: 'Route', title: 'Route by country', body: 'Send callers to the right language team or region automatically.' },
      { icon: 'BadgeCheck', title: 'ITU regulated', body: 'Allocated by the ITU under global numbering standards.' },
      { icon: 'RefreshCw', title: 'Failover', body: 'Move calls to a backup centre if the primary is unavailable.' },
      { icon: 'ChartColumn', title: 'Reporting by country', body: 'See where your calls come from and how they were handled.' },
    ],
    useCases: [
      { icon: 'Building2', title: 'Multinationals', body: 'One number for every operation.' },
      { icon: 'Headphones', title: 'Global support', body: 'Free helplines across borders.' },
      { icon: 'Hotel', title: 'Travel and hospitality', body: 'Reachable wherever guests travel.' },
      { icon: 'Briefcase', title: 'Enterprise sales', body: 'A global hotline for buyers.' },
    ],
    faqs: [
      { q: 'How many countries can call a UIFN?', a: 'UIFNs are reachable from 45+ participating countries. The live list below shows where VOIP2 can activate yours, and reachability from mobile networks varies by country.' },
      { q: 'How is a UIFN different from a toll-free number?', a: 'A toll-free number is free to call within one country. A UIFN is one +800 number that is free to call from many countries.' },
      { q: 'How long does a UIFN take to set up?', a: 'UIFNs are registered with the ITU and then activated in each country you choose, so they take longer than a local number. We manage the registration and keep you updated.' },
      COMMON_FAQS[2],
    ],
    countryOptions: ['Europe', 'Americas', 'Asia-Pacific', 'Middle East & Africa', 'Worldwide'],
    countryLabel: 'Where are your callers?',
    service: 'UIFN Numbers',
    related: related('tollfree', 'did', 'pbx'),
  },
};

export const NUMBER_SCREENS = SCREENS;
export const NUMBER_STEPS = STEPS;
